import { useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import Wordmark from "@/components/Wordmark";

const emailSchema = z.string().trim().email("Enter a valid email address").max(255);

const GENERIC_MESSAGE =
  "If an account exists for that address, we've sent a link to reset the password. Check your inbox and spam folder.";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      return;
    }
    setError(null);
    setSubmitting(true);
    // Never surface provider errors: the response must not reveal whether the
    // address has an account.
    await supabase.auth.resetPasswordForEmail(parsed.data, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setSubmitting(false);
    setSent(true);
  };

  return (
    <div className="min-h-screen bg-gradient-warm flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-card rounded-[1.5rem] shadow-bold p-8">
        <Link to="/" className="flex justify-center mb-6">
          <Wordmark size="md" />
        </Link>
        <h1 className="font-display text-3xl text-center">Reset your password</h1>
        <p className="text-sm text-muted-foreground text-center mt-2">
          Enter the email address you use to sign in and we'll send a reset link.
        </p>

        {sent ? (
          <div className="mt-6 space-y-4">
            <p role="status" className="text-sm text-foreground">{GENERIC_MESSAGE}</p>
            <Link
              to="/auth"
              className="block w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold text-center hover:bg-primary transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-6" noValidate>
            <div>
              <label htmlFor="reset-email" className="text-xs font-semibold uppercase tracking-wider">
                Email
              </label>
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                maxLength={255}
                autoComplete="email"
                className="w-full mt-1 px-4 py-3 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-primary transition-colors disabled:opacity-50"
            >
              {submitting ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        <Link to="/auth" className="block mt-6 text-center text-xs text-muted-foreground hover:text-primary">
          ← Back to sign in
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
